/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useImpostazioni, urlImmagine } from '../hooks/useImpostazioni';
import { authAPI } from '../api/auth';

const DASHBOARD_CONFIG = {
  title: 'Dashboard Cliente',
  accentColor: '#3B82F6',
  expectedRole: 'cliente',
  redirectTo: '/dashboard',
};

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [nonVerificata, setNonVerificata] = useState(false);
  const [resendStato, setResendStato] = useState(''); // '' | 'invio' | 'ok'
  const { login } = useAuth();
  const impostazioni = useImpostazioni();
  const logo = urlImmagine(impostazioni.app_logo_url);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNonVerificata(false);
    setResendStato('');
    setLoading(true);

    try {
      const user = await login(email, password);

      if (user.role !== DASHBOARD_CONFIG.expectedRole) {
        setError(`Accesso negato. Questa dashboard è riservata agli utenti con ruolo "${DASHBOARD_CONFIG.expectedRole}".`);
        setLoading(false);
        return;
      }

      navigate(DASHBOARD_CONFIG.redirectTo);
    } catch (err) {
      // Email non ancora verificata: il backend risponde 403 (NON 401, per non far
      // scattare il redirect dell'interceptor axios) con code EMAIL_NON_VERIFICATA.
      if (err.response?.status === 403 || err.response?.data?.code === 'EMAIL_NON_VERIFICATA') {
        setNonVerificata(true);
        setError('Devi verificare la tua email prima di accedere.');
      } else {
        setError(err.response?.data?.message || err.response?.data?.error || 'Email o password non validi');
      }
      setLoading(false);
    }
  };

  const reinviaVerifica = async () => {
    setResendStato('invio');
    try {
      await authAPI.resendVerification(email);
      setResendStato('ok');
    } catch {
      setResendStato('');
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {logo && <img src={logo} alt="" style={styles.logo} />}
        <h1 style={{ ...styles.title, color: DASHBOARD_CONFIG.accentColor }}>
          {DASHBOARD_CONFIG.title}
        </h1>
        <p style={styles.subtitle}>{impostazioni.app_nome}</p>
        {impostazioni.app_descrizione && <p style={styles.descrizione}>{impostazioni.app_descrizione}</p>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              style={styles.input}
            />
          </label>

          <label style={styles.label}>
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={styles.input}
            />
          </label>

          {error && <div style={styles.error}>{error}</div>}

          {nonVerificata && (
            <button
              type="button"
              onClick={reinviaVerifica}
              disabled={resendStato !== ''}
              style={styles.resendBtn}
            >
              {resendStato === 'ok' ? 'Email di verifica reinviata ✓' : resendStato === 'invio' ? 'Invio...' : 'Reinvia email di verifica'}
            </button>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...styles.button,
              backgroundColor: DASHBOARD_CONFIG.accentColor,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Accesso in corso...' : 'Accedi'}
          </button>
        </form>

        <div style={{ marginTop: '0.9rem', textAlign: 'center' }}>
          <Link to="/password-dimenticata" style={{ color: '#3B82F6', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
            Password dimenticata?
          </Link>
        </div>

        <div style={styles.registerRow}>
          Non hai un account?{' '}
          <Link to="/registrazione" style={{ ...styles.registerLink, color: DASHBOARD_CONFIG.accentColor }}>Registrati</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F3F4F6',
    padding: '1rem',
  },
  card: {
    backgroundColor: 'white',
    padding: '2.5rem',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '400px',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: '700',
    margin: 0,
    textAlign: 'center',
  },
  logo: { display: 'block', maxWidth: '120px', maxHeight: '80px', objectFit: 'contain', margin: '0 auto 1rem' },
  descrizione: { textAlign: 'center', fontSize: '0.82rem', color: '#9CA3AF', marginTop: '-1.7rem', marginBottom: '2rem' },
  subtitle: {
    textAlign: 'center',
    color: '#6B7280',
    marginTop: '0.25rem',
    marginBottom: '2rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
  },
  input: {
    padding: '0.65rem 0.85rem',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '1rem',
    outline: 'none',
  },
  error: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
    padding: '0.75rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
  },
  button: {
    padding: '0.85rem',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    marginTop: '0.5rem',
  },
  resendBtn: {
    padding: '0.7rem',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    backgroundColor: 'white',
    color: '#374151',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  registerRow: {
    marginTop: '1.5rem',
    textAlign: 'center',
    fontSize: '0.9rem',
    color: '#6B7280',
  },
  registerLink: {
    fontWeight: 600,
    textDecoration: 'none',
  },
};
